import { z } from 'zod';
// ---------------------------------------------------------------------------
// Kit destruction protocol print (Domain 7 — Document & PDF Engine, issue #31
// B1; REQ-DOC-018 on Domain 5 ledger data, REQ-KIT-032)
// ---------------------------------------------------------------------------
/**
 * Body of `POST /api/v1/documents/kit-destruction/generate` and of the
 * render-pdf counterpart (REQ-DOC-018).
 *
 * ONE identifier — the D5 destruction protocol. Everything else (site, trial
 * laboratory, destroying person, per-item performers, kit names, expiration
 * dates) is resolved by the composition service through the exported read
 * doors of the kits/personnel clusters — the same single-id contract as VCL
 * (`GenerateVclRequestSchema`) and the no-cross-cluster-SQL rule.
 */
export const GenerateKitDestructionRequestSchema = z.object({
    kitOutProtocolId: z.number().int().positive(),
});
/**
 * The A4 header of the protocol (REQ-DOC-018; ledger fields per legacy
 * dbase-v1.2 s.26/31–32: Protocol_date, Personel_id, #Laboratory_id,
 * Comments). Values are display-resolved — the template never touches ids
 * (VCL header convention). No signature blanks or logotypes: the legacy
 * evidence has none (feature acceptance decision, 2026-10-18).
 */
export const KitDestructionHeaderSchema = z.object({
    /** Display identifier formatted by the service from the ledger (e.g. `PROT/2026/003`). */
    protocolNumber: z.string().min(1),
    /** Date of the physical destruction (`kit_out_protocols.protocol_date`). */
    protocolDate: z.iso.date(),
    /** Name of the site performing the destruction. */
    siteName: z.string().min(1),
    /** Trial laboratory of the protocol (`trial_laboratory_id` resolved to its name). */
    laboratoryName: z.string().min(1),
    /** Person destroying the kits; null when the ledger user no longer resolves — printed empty. */
    destroyedByName: z.string().nullable(),
    /** Optional cause/comment; null → the protocol prints without a filled cause (feature edge). */
    comments: z.string().nullable(),
});
/**
 * One printed row of the kit table (REQ-DOC-018, REQ-KIT-032): one `kit_out`
 * item permanently taken off stock by this protocol. The optional patient
 * assignment of `kit_out` deliberately does NOT travel — the printout carries
 * no patient data (feature acceptance decision, 2026-10-18; REQ-DOC-021/023).
 */
export const KitDestructionItemSchema = z.object({
    /** Kit type name (`lab_kit_id` resolved). */
    kitName: z.string().min(1),
    /** Expiration date; null when the kit has none — the template prints the field empty. */
    expirationDate: z.iso.date().nullable(),
    numberOfKits: z.number().int().positive(),
    /** Date the item was taken off stock (`kit_out.out_date`). */
    outDate: z.iso.date(),
    /** Person who performed the take-off; null when unresolvable — printed empty. */
    performedByName: z.string().nullable(),
    /** Optional per-item comment. */
    comments: z.string().nullable(),
});
/**
 * The composed destruction-protocol document returned by the generation
 * endpoint and carried as the render-job payload — everything the A4 template
 * renders (mirrors `VclDocumentDtoSchema`). A protocol always has at least
 * one item (enforced at creation, `CreateKitOutProtocolRequestSchema`).
 *
 * Sub-shapes are reachable through indexed access
 * (`KitDestructionDocumentDto['header']` …); dedicated sub-types are
 * deliberately NOT exported, mirroring the VCL convention.
 */
export const KitDestructionDocumentDtoSchema = z.object({
    kitOutProtocolId: z.number().int().positive(),
    header: KitDestructionHeaderSchema,
    items: z.array(KitDestructionItemSchema).min(1),
});
//# sourceMappingURL=kit-destruction.schema.js.map